import { trackRiskLoginModalLoginWithSsoClick } from "@faire/web-api--source/events/risk/click/loginModalLoginWithSso";
import { ISignUpSuggestions } from "@faire/web-api--source/indigofair/data/ISignUpSuggestions";
import { ISSOToken } from "@faire/web-api--source/indigofair/data/ISSOToken";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { useCallback, useMemo } from "react";

import { ssoCache } from "@faire/retailer/features/SignUp/SSORequestData";

import {
  BLANK_SIGN_UP_SUGGESTIONS,
  useSSOContextStore,
} from "./SSOContextStore";

export const useSSOStore = () => {
  const [
    {
      signUpSuggestions,
      isActive,
      userClickedSSOButton,
      id_token,
      provider,
      promptSSO,
    },
    setStore,
  ] = useSSOContextStore([
    "signUpSuggestions",
    "isActive",
    "userClickedSSOButton",
    "id_token",
    "provider",
    "promptSSO",
  ]);

  const ssoToken: ISSOToken = useMemo(() => {
    return {
      id_token: id_token || ssoCache.get()?.id_token,
      provider:
        provider === "SOCIAL_SIGN_ON_PROVIDER_UNKNOWN"
          ? ssoCache.get()?.provider
          : provider,
    };
  }, [id_token, provider]);

  const reset = useCallback(() => {
    setStore({
      signUpSuggestions: BLANK_SIGN_UP_SUGGESTIONS,
      id_token: "",
      isActive: false,
    });
  }, [setStore]);

  const set = useCallback(
    (
      signUpSuggestions: ISignUpSuggestions | undefined,
      id_token: string,
      provider: SocialSignOnProvider
    ) => {
      if (signUpSuggestions) {
        setStore({
          signUpSuggestions,
        });
      }

      setStore({
        id_token,
        provider,
        isActive: true,
      });

      ssoCache.save({
        signUpSuggestions,
        id_token,
        provider,
        isActive: true,
      });
    },
    [setStore]
  );

  const setPromptSSO = useCallback(
    (promptSSO: boolean) => {
      setStore({
        promptSSO,
      });
    },
    [setStore]
  );

  const recordSSOButtonClick = useCallback(() => {
    trackRiskLoginModalLoginWithSsoClick();

    setStore({
      userClickedSSOButton: true,
    });
  }, [setStore]);

  return {
    selectedProvider: provider,
    signUpSuggestions,
    isActive,
    userClickedSSOButton,
    id_token,
    promptSSO,
    ssoToken,
    reset,
    set,
    setPromptSSO,
    recordSSOButtonClick,
  };
};
